import { c as _c } from "react/compiler-runtime";
import classNames from 'classnames';
import { forwardRef } from 'react';
import type { ReactNode } from 'react';
import TextField from '../TextField';
import type { TextFieldProps } from '../TextField';
import styles from './PhoneField.module.scss';
export type PhoneFieldProps = Omit<TextFieldProps, 'type'> & {
  /**
   * The country picker, in the field's start cell. Its behaviour stays in
   * app code: a SelectField (drawn without its own edge here) or the app's
   * own control, named by its own label.
   */
  countryControl: ReactNode;
};

/**
 * A phone number field: the `countryControl` slot in a start cell, then a
 * TextField `type="tel"` that takes every other prop, the forwarded ref and
 * FormGroup's wiring. The pair draws one shared edge, its focus ring and
 * its error state. Formatting stays in app code (`renderInput`, as on
 * TextField).
 */
const PhoneField = forwardRef<HTMLInputElement, PhoneFieldProps>(function PhoneField(t0, ref) {
  const $ = _c(16);
  let countryControl;
  let props;
  let renderInput;
  if ($[0] !== t0) {
    const {
      countryControl: t1,
      renderInput: t2,
      className: _className,
      style: _style,
      ...t3
    } = t0;
    countryControl = t1;
    renderInput = t2;
    props = t3;
    $[0] = t0;
    $[1] = countryControl;
    $[2] = props;
    $[3] = renderInput;
  } else {
    countryControl = $[1];
    props = $[2];
    renderInput = $[3];
  }
  let t1;
  if ($[4] === Symbol.for("react.memo_cache_sentinel")) {
    t1 = classNames(styles["helios-phone-field__input"]);
    $[4] = t1;
  } else {
    t1 = $[4];
  }
  const inputClassName = t1;
  let t2;
  if ($[5] !== countryControl) {
    t2 = <div className={styles["helios-phone-field__country"]}>{countryControl}</div>;
    $[5] = countryControl;
    $[6] = t2;
  } else {
    t2 = $[6];
  }
  let t3;
  if ($[7] !== renderInput) {
    t3 = inputProps => {
      const phoneInputProps = {
        ...inputProps,
        className: inputClassName
      };
      return renderInput ? renderInput(phoneInputProps) : <input {...phoneInputProps} />;
    };
    $[7] = renderInput;
    $[8] = t3;
  } else {
    t3 = $[8];
  }
  let t4;
  if ($[9] !== props || $[10] !== ref || $[11] !== t3) {
    t4 = <TextField {...props} ref={ref} type="tel" renderInput={t3} />;
    $[9] = props;
    $[10] = ref;
    $[11] = t3;
    $[12] = t4;
  } else {
    t4 = $[12];
  }
  let t5;
  if ($[13] !== t2 || $[14] !== t4) {
    t5 = <div data-helios-control="phone" className={styles["helios-phone-field"]}>{t2}{t4}</div>;
    $[13] = t2;
    $[14] = t4;
    $[15] = t5;
  } else {
    t5 = $[15];
  }
  return t5;
});
export default PhoneField;